import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Container, Input } from "reactstrap";
import { Upload, RefreshCw, GitBranch, Menu as MenuIcon, Grid, Download, CheckCircle, X, RotateCcw } from "react-feather";

import placeholder from "../../assets/images/image-placeholder.svg";
import { errorToast, successToast } from "../../shared/Toast";
import {
  getMerchantByID,
  getMerchantDetails,
  getUserListing,
  refreshMerchantDetails,
  resyncAvailability,
  syncBranches,
  syncMenu,
  syncTables,
  fetchSystemProducts,
} from "../../utils/apiCalls";
import Breadcrumb from "../common/breadcrumb";
import axiosInstance from "../../utils/axios";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, SectionTitle, Loader as RLoader } from "../reporting/ReportingShared";
import { buildMerchantChanges } from "./merchantChanges";
import MerchantChangesReviewModal from "./MerchantChangesReviewModal";
import { PAGES_BASE_URL, slugError } from "../../constants/merchantPages";

const INITIAL = {
  customer: "",
  name: "",
  reference: "",
  slug: "",
  url: "",
  email: "",
  password: "",
  passwordSet: false,
  db: "",
  clientId: "",
  clientSecret: "",
  isLoyaltyActive: false,
  isWalletActive: false,
  accessToken: null,
  accessTokenExpiry: "",
  koinzPosId: "",
  koinzAccessKey: "",
  koinzMerchantKey: "",
  blendBrandId: "",
  blendMerchantEmail: "",
  blendMerchantPassword: "",
  blendSyncAvailability: true,
  email_pending_orders_count: 0,
  staleDeviceOrdersAlertDays: 0,
  pendingOrdersCancellationMinutes: 0,
  autoReadyMinutes: 0,
  autoDeliveredMinutes: 0,
  acceptNormalOrdersOnCreation: false,
  customerFeedbackEnabled: false,
  customerFeedbackContactRequired: false,
  // Marketing frequency caps; "" = platform default (3 per rolling 7 days).
  pushCampaignWeeklyLimit: "",
  walletBroadcastWeeklyLimit: "",
  carPickupActive: false,
  saveCards: false,
  moyasarSecretKey: "",
  moyasarSecretKeySet: false,
  moyasarWebhookSecret: "",
  moyasarWebhookSecretSet: false,
  moyasarPublishableKey: "",
  paymentGateway: "moyasar",
  telrStoreId: "",
  telrApiKey: "",
  telrApiKeySet: false,
  telrRemoteAuthKey: "",
  telrRemoteAuthKeySet: false,
  telrHostedAuthKey: "",
  telrHostedAuthKeySet: false,
  telrWalletAuthKey: "",
  telrWalletAuthKeySet: false,
  telrTestMode: false,
  applePayWebEnabled: false,
  applePayMerchantId: "",
  applePayDisplayName: "",
  applePayIdentityCert: "",
  applePayIdentityKey: "",
  applePayIdentitySet: false,
  applePayIdentityDaysLeft: null,
  googleMapsApiKey: "",
  googleMapsApiKeySet: false,
  currency: "SAR",
  forceOnlineOrdersAsDelivery: false,
  menuColor: "#ffffff",
  kdsDisplayColor: "#ffffff",
  kdsDisplayImage: "",
  receiptLogo: "",
  enableMenuOrdering: false,
  enableMenuPagination: false,
  disableBackToMenus: false,
  enableBranchProductsAlternatives: false,
  enableProductRecommendations: false,
  enableCartTokenReapply: true,
  blockOrderOnUnavailableItems: false,
  staleTableWarningMinutes: 180,
  timezone: "Asia/Riyadh",
  branchesPlaceholderImage: "",
  branchesListColumnsPickupApp: 1,
  loyaltyCardLockedImage: "",
  loyaltyCardUnlockedImage: "",
  productPlaceholderImage: "",
  favicon: "",
  enablePosLoyaltyOrders: false,
  autoCreatePosLoyaltyCustomers: false,
  restoreRewardOnPosReturn: false,
  notificationTarget: "pos",
  enableCustomerArrival: false,
  waitlistOtpRequired: false,
  coupons_enabled: false,
  sendTemplatesToPickupApp: true,
  virtual_deliverect_sku: false,
  hideAppOrdersAnalytics: false,
  posReceiptTemplatesEnabled: false,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  background: C.white,
  color: C.text,
  outline: "none",
  fontFamily: "inherit",
  transition: "border 0.15s",
};
const inputDisabledStyle = { ...inputStyle, background: C.bg, color: C.textMed };

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: C.textMed,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
};

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {hint && (
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{hint}</div>
    )}
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 14px",
      borderRadius: 10,
      border: `1px solid ${C.borderLight}`,
      background: C.bg,
      marginBottom: 10,
    }}
  >
    <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
    <ToggleSwitch checked={checked} handleToggle={onChange} />
  </div>
);

// ✕ next to a stored secret. Marks it for removal on Save; pressing again undoes that.
const RemoveSecretButton = ({ pending, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={pending ? "Keep the stored value" : "Remove the stored value on save"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "0 12px",
      borderRadius: 8,
      background: C.white,
      border: `1px solid ${C.border}`,
      color: pending ? C.textMed : C.coral,
      fontWeight: 600,
      fontSize: 12,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    {pending ? <RotateCcw size={14} /> : <X size={14} />}
    {pending ? "Undo" : "Remove"}
  </button>
);

const ActionButton = ({ icon: Icon, label, onClick, color, disabled, loading: btnLoading }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 16px",
      borderRadius: 10,
      border: "none",
      background: `linear-gradient(135deg, ${color}, ${color}cc)`,
      color: C.white,
      fontWeight: 600,
      fontSize: 12,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      boxShadow: `0 4px 12px ${color}40`,
      transition: "transform 0.15s",
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = "scale(1.03)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
  >
    <Icon size={14} className={btnLoading ? "rpt-spin" : ""} />
    {label}
  </button>
);

// NOTE: Wallet Pass / loyalty-card branding (colors + icons) now lives on the
// dedicated "Loyalty → Card Design" page (components/loyalty/card-design.js),
// which provides a live preview. It is the single source of truth for that
// config, so the fields were removed from this page.

const EditMerchant = () => {
  const navigate = useNavigate();
  const { merchantID } = useParams();

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [merchantData, setMerchantData] = useState(INITIAL);
  // UI-only, never submitted. By default only the ACTIVE gateway's credentials are
  // shown, but a staged rollout needs to enter the new gateway's keys BEFORE flipping
  // to it — so this reveals both blocks on demand.
  const [showAllGateways, setShowAllGateways] = useState(false);
  // What the API will disclose about the Koinz credentials — never the values themselves.
  const [koinzStatus, setKoinzStatus] = useState({
    posIdMasked: null,
    accessKeySet: false,
    merchantKeySet: false,
  });
  // Same for Blend: the API reports only what is configured, never a secret.
  const [blendStatus, setBlendStatus] = useState({
    brandId: null,
    merchantEmail: null,
    merchantPasswordSet: false,
    sessionSet: false,
    sessionFetchedAt: null,
  });
  const [merchantBranches, setMerchantBranches] = useState([]);
  const [previewImage, setPreviewImage] = useState(placeholder);
  const [imageFile, setImageFile] = useState(null);
  const [branchesPlaceholderPreview, setBranchesPlaceholderPreview] = useState(placeholder);
  const [branchesPlaceholderFile, setBranchesPlaceholderFile] = useState(null);
  const [loyaltyLockedPreview, setLoyaltyLockedPreview] = useState(placeholder);
  const [loyaltyLockedFile, setLoyaltyLockedFile] = useState(null);
  const [loyaltyUnlockedPreview, setLoyaltyUnlockedPreview] = useState(placeholder);
  const [loyaltyUnlockedFile, setLoyaltyUnlockedFile] = useState(null);
  const [productPlaceholderPreview, setProductPlaceholderPreview] = useState(placeholder);
  const [productPlaceholderFile, setProductPlaceholderFile] = useState(null);
  const [kdsDisplayPreview, setKdsDisplayPreview] = useState(placeholder);
  const [kdsDisplayFile, setKdsDisplayFile] = useState(null);
  const [receiptLogoPreview, setReceiptLogoPreview] = useState(placeholder);
  const [receiptLogoFile, setReceiptLogoFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(placeholder);
  const [faviconFile, setFaviconFile] = useState(null);
  const [syncing, setSyncing] = useState({});
  // merchantData exactly as loaded; the review modal diffs against it.
  const [original, setOriginal] = useState(null);
  // Stored secrets marked with ✕. A blank secret input still means "keep".
  const [clearSecrets, setClearSecrets] = useState(() => new Set());
  const [reviewRows, setReviewRows] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) =>
    setMerchantData((prev) => ({ ...prev, [key]: value }));

  const toggleClearSecret = (key) => {
    setClearSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // A pending removal and a typed replacement can't both be sent.
    setField(key, "");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFilePreviewChange = (setFile, setPreview) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const formatExpiry = (seconds) => {
    if (!seconds || seconds <= 0) return "Expired";
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const parts = [];
    if (d) parts.push(`${d} day${d > 1 ? "s" : ""}`);
    if (h) parts.push(`${h} hour${h > 1 ? "s" : ""}`);
    if (m) parts.push(`${m} minute${m > 1 ? "s" : ""}`);
    if (s && !d && !h) parts.push(`${s} second${s > 1 ? "s" : ""}`);
    return parts.join(", ") || "Expired";
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getMerchantByID(merchantID);
        if (res.isError === false) {
          const data = res?.data;
          let deliveryPartner = {};
          if (data?.deliveryPartner && typeof data.deliveryPartner === "string") {
            try { deliveryPartner = JSON.parse(data.deliveryPartner); } catch { }
          } else if (data?.deliveryPartner) {
            deliveryPartner = data.deliveryPartner;
          }

          // Koinz credentials are never sent back by the API — only whether each one is
          // set, plus a masked pos_id. The inputs therefore start blank, and a blank box
          // on save means "leave unchanged".
          setKoinzStatus({
            posIdMasked: data?.koinzPosIdMasked || null,
            accessKeySet: !!data?.koinzAccessKeySet,
            merchantKeySet: !!data?.koinzMerchantKeySet,
          });

          // Blend: same convention. Non-secret values (brand, the two login
          // emails) come back and prefill; passwords never do.
          setBlendStatus({
            brandId: data?.blendPartnerStatus?.brandId || null,
            merchantEmail: data?.blendPartnerStatus?.merchantEmail || null,
            merchantPasswordSet: !!data?.blendPartnerStatus?.merchantPasswordSet,
            sessionSet: !!data?.blendPartnerStatus?.sessionSet,
            sessionFetchedAt: data?.blendPartnerStatus?.sessionFetchedAt || null,
          });


          const loaded = {
            customer: data?.customerId || "",
            name: data?.name || "",
            reference: data?.reference || "",
            slug: data?.slug || "",
            url: data?.url || "",
            email: data?.email || "",
            // Odoo password never comes back from the API; blank = keep the stored one.
            password: "",
            passwordSet: !!data?.passwordSet,
            db: data?.db || "",
            menuColor: data?.menuColor || "#ffffff",
            kdsDisplayColor: data?.kdsDisplayColor || "#ffffff",
            kdsDisplayImage: data?.kdsDisplayImage || "",
            receiptLogo: data?.receiptLogo || "",
            enableMenuOrdering: data?.enableMenuOrdering || false,
            enableMenuPagination: data?.enableMenuPagination ?? false,
            disableBackToMenus: data?.disableBackToMenus ?? false,
            enableBranchProductsAlternatives: data?.enableBranchProductsAlternatives ?? false,
            enableProductRecommendations: data?.enableProductRecommendations ?? false,
            enableCartTokenReapply: data?.enableCartTokenReapply ?? true,
            blockOrderOnUnavailableItems: data?.blockOrderOnUnavailableItems ?? false,
            forceOnlineOrdersAsDelivery: data?.forceOnlineOrdersAsDelivery ?? false,
            clientId: deliveryPartner?.clientId || "",
            clientSecret: deliveryPartner?.clientSecret || "",
            isLoyaltyActive: data?.enableLoyaltyProgram || false,
            isMembershipActive: data?.enableMembershipProgram || false,
            isWalletActive: data?.enableWallet || false,
            carPickupActive: data?.enableCarPickupOption || false,
            saveCards: data?.saveCards || false,
            moyasarSecretKey: "",
            moyasarSecretKeySet: data?.moyasarSecretKeySet || false,
            moyasarWebhookSecret: "",
            moyasarWebhookSecretSet: data?.moyasarWebhookSecretSet || false,
            moyasarPublishableKey: data?.moyasarPublishableKey || "",
            paymentGateway: data?.paymentGateway || "moyasar",
            telrStoreId: data?.telrStoreId || "",
            telrApiKey: "",
            telrApiKeySet: data?.telrApiKeySet || false,
            telrRemoteAuthKey: "",
            telrRemoteAuthKeySet: data?.telrRemoteAuthKeySet || false,
            telrHostedAuthKey: "",
            telrHostedAuthKeySet: data?.telrHostedAuthKeySet || false,
            telrWalletAuthKey: "",
            telrWalletAuthKeySet: data?.telrWalletAuthKeySet || false,
            telrTestMode: data?.telrTestMode || false,
            applePayWebEnabled: data?.applePayWebEnabled || false,
            applePayMerchantId: data?.applePayMerchantId || "",
            applePayDisplayName: data?.applePayDisplayName || "",
            // The PEMs are never sent to the browser; blank means "keep the stored pair".
            applePayIdentityCert: "",
            applePayIdentityKey: "",
            applePayIdentitySet: data?.applePayIdentitySet || false,
            applePayIdentityDaysLeft: data?.applePayIdentityDaysLeft ?? null,
            googleMapsApiKey: "",
            googleMapsApiKeySet: data?.googleMapsApiKeySet || false,
            currency: data?.currency || "SAR",
            email_pending_orders_count: data?.email_pending_orders_count || 0,
            staleDeviceOrdersAlertDays: data?.staleDeviceOrdersAlertDays ?? 0,
            pendingOrdersCancellationMinutes: data?.pendingOrdersCancellationMinutes || 0,
            autoReadyMinutes: data?.autoReadyMinutes || 0,
            autoDeliveredMinutes: data?.autoDeliveredMinutes || 0,
            acceptNormalOrdersOnCreation: data?.acceptNormalOrdersOnCreation || false,
            customerFeedbackEnabled: data?.customerFeedbackEnabled || false,
            customerFeedbackContactRequired: data?.customerFeedbackContactRequired || false,
            pushCampaignWeeklyLimit: data?.pushCampaignWeeklyLimit ?? "",
            walletBroadcastWeeklyLimit: data?.walletBroadcastWeeklyLimit ?? "",
            staleTableWarningMinutes: data?.staleTableWarningMinutes ?? 180,
            timezone: data?.timezone || "Asia/Riyadh",
            accessToken: deliveryPartner?.token?.access_token || null,
            accessTokenExpiry: deliveryPartner?.token?.expires_in || null,
            koinzPosId: "",
            koinzAccessKey: "",
            koinzMerchantKey: "",
            // Non-secret Blend values prefill; the two passwords stay blank,
            // where blank means "leave unchanged" on save.
            blendBrandId: data?.blendPartnerStatus?.brandId || "",
            blendMerchantEmail: data?.blendPartnerStatus?.merchantEmail || "",
            blendMerchantPassword: "",
            // Absent means on (API reports it already resolved).
            blendSyncAvailability: data?.blendPartnerStatus?.syncAvailability !== false,
            branchesPlaceholderImage: data?.branchesPlaceholderImage || "",
            branchesListColumnsPickupApp: data?.branchesListColumnsPickupApp ?? 1,
            loyaltyCardLockedImage: data?.loyaltyCardLockedImage || "",
            loyaltyCardUnlockedImage: data?.loyaltyCardUnlockedImage || "",
            productPlaceholderImage: data?.productPlaceholderImage || "",
            favicon: data?.favicon || "",
            enablePosLoyaltyOrders: data?.enablePosLoyaltyOrders ?? false,
            autoCreatePosLoyaltyCustomers: data?.autoCreatePosLoyaltyCustomers ?? false,
            restoreRewardOnPosReturn: data?.restoreRewardOnPosReturn ?? false,
            notificationTarget: data?.notificationTarget || "pos",
            enableCustomerArrival: data?.enableCustomerArrival ?? false,
            waitlistOtpRequired: data?.waitlistOtpRequired ?? false,
            coupons_enabled: data?.coupons_enabled ?? false,
            sendTemplatesToPickupApp: data?.sendTemplatesToPickupApp ?? true,
            virtual_deliverect_sku: data?.virtual_deliverect_sku ?? false,
            hideAppOrdersAnalytics: data?.hideAppOrdersAnalytics ?? false,
            posReceiptTemplatesEnabled: data?.posReceiptTemplatesEnabled ?? false,
            // Not submitted; kept so the review can tell "replaced" from "added".
            image1: data?.image1 || "",
          };
          setMerchantData(loaded);
          setOriginal(loaded);
          setClearSecrets(new Set());

          setMerchantBranches(data?.Branches || []);
          if (data?.image1) setPreviewImage(process.env.REACT_APP_URL + data.image1);
          if (data?.branchesPlaceholderImage) setBranchesPlaceholderPreview(process.env.REACT_APP_URL + data.branchesPlaceholderImage);
          if (data?.loyaltyCardLockedImage) setLoyaltyLockedPreview(process.env.REACT_APP_URL + data.loyaltyCardLockedImage);
          if (data?.loyaltyCardUnlockedImage) setLoyaltyUnlockedPreview(process.env.REACT_APP_URL + data.loyaltyCardUnlockedImage);
          if (data?.productPlaceholderImage) setProductPlaceholderPreview(process.env.REACT_APP_URL + data.productPlaceholderImage);
          if (data?.kdsDisplayImage) setKdsDisplayPreview(process.env.REACT_APP_URL + data.kdsDisplayImage);
          if (data?.receiptLogo) setReceiptLogoPreview(process.env.REACT_APP_URL + data.receiptLogo);
          if (data?.favicon) setFaviconPreview(process.env.REACT_APP_URL + data.favicon);

          try {
            const users = await getUserListing();
            const filtered = users?.filter((u) => u?.MerchantId === parseInt(merchantID));
            setUserData(filtered || []);
          } catch (e) {
            console.error("Error fetching users:", e);
          }
        }
      } catch (error) {
        console.error("Error fetching merchant data:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [merchantID]);

  // Whether each secret is stored right now, for the review's "Configured / Not set".
  const secretStatus = {
    password: merchantData.passwordSet,
    koinzPosId: !!koinzStatus.posIdMasked,
    koinzAccessKey: koinzStatus.accessKeySet,
    koinzMerchantKey: koinzStatus.merchantKeySet,
    blendMerchantPassword: blendStatus.merchantPasswordSet,
    googleMapsApiKey: merchantData.googleMapsApiKeySet,
    moyasarSecretKey: merchantData.moyasarSecretKeySet,
    moyasarWebhookSecret: merchantData.moyasarWebhookSecretSet,
    telrApiKey: merchantData.telrApiKeySet,
    telrRemoteAuthKey: merchantData.telrRemoteAuthKeySet,
    telrHostedAuthKey: merchantData.telrHostedAuthKeySet,
    telrWalletAuthKey: merchantData.telrWalletAuthKeySet,
    applePayIdentityCert: merchantData.applePayIdentitySet,
    applePayIdentityKey: merchantData.applePayIdentitySet,
  };

  // Save is gated: the form's submit (button or Enter in any input) only opens the
  // review. Nothing is sent until the admin confirms there.
  const handleSubmit = (e) => {
    e.preventDefault();
    const slugProblem = slugError(merchantData.slug, { merchant: true, required: false });
    if (slugProblem) {
      errorToast(`URL Slug: ${slugProblem}`);
      return;
    }
    const rows = buildMerchantChanges({
      original,
      current: merchantData,
      files: {
        image1: imageFile,
        favicon: faviconFile,
        receiptLogo: receiptLogoFile,
        kdsDisplayImage: kdsDisplayFile,
        branchesPlaceholderImage: branchesPlaceholderFile,
        loyaltyCardLockedImage: loyaltyLockedFile,
        loyaltyCardUnlockedImage: loyaltyUnlockedFile,
        productPlaceholderImage: productPlaceholderFile,
      },
      clearSecrets,
      secretStatus,
      customerLabel: (id) => {
        const u = userData.find((c) => String(c.id) === String(id));
        return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : String(id);
      },
    });
    if (!rows.length) {
      errorToast("No changes to save");
      return;
    }
    setReviewRows(rows);
    setReviewOpen(true);
  };

  const doSave = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("customerId", merchantData.customer);
      formData.append("name", merchantData.name);
      formData.append("url", merchantData.url);
      // Empty clears it; the API refuses that while the merchant has published pages.
      formData.append("slug", String(merchantData.slug || "").trim().toLowerCase());
      formData.append("email", merchantData.email);
      if (merchantData.password) formData.append("password", merchantData.password);
      formData.append("db", merchantData.db);
      formData.append("menuColor", merchantData.menuColor);
      formData.append("kdsDisplayColor", merchantData.kdsDisplayColor);
      formData.append("enableMenuOrdering", merchantData.enableMenuOrdering);
      formData.append("enableMenuPagination", merchantData.enableMenuPagination);
      formData.append("disableBackToMenus", merchantData.disableBackToMenus);
      formData.append("enableBranchProductsAlternatives", merchantData.enableBranchProductsAlternatives);
      formData.append("enableProductRecommendations", merchantData.enableProductRecommendations);
      formData.append("enableCartTokenReapply", merchantData.enableCartTokenReapply);
      formData.append("blockOrderOnUnavailableItems", merchantData.blockOrderOnUnavailableItems);
      formData.append("enableLoyaltyProgram", merchantData.isLoyaltyActive);
      formData.append("enableMembershipProgram", merchantData.isMembershipActive);
      formData.append("enableWallet", merchantData.isWalletActive);
      formData.append("email_pending_orders_count", merchantData.email_pending_orders_count);
      formData.append("staleDeviceOrdersAlertDays", merchantData.staleDeviceOrdersAlertDays ?? 0);
      formData.append("pendingOrdersCancellationMinutes", merchantData.pendingOrdersCancellationMinutes);
      formData.append("autoReadyMinutes", merchantData.autoReadyMinutes);
      formData.append("autoDeliveredMinutes", merchantData.autoDeliveredMinutes);
      formData.append("acceptNormalOrdersOnCreation", merchantData.acceptNormalOrdersOnCreation);
      formData.append("customerFeedbackEnabled", merchantData.customerFeedbackEnabled);
      formData.append("customerFeedbackContactRequired", merchantData.customerFeedbackContactRequired);
      // Sent even when empty: "" tells the server to clear the override (NULL = default).
      formData.append("pushCampaignWeeklyLimit", merchantData.pushCampaignWeeklyLimit ?? "");
      formData.append("walletBroadcastWeeklyLimit", merchantData.walletBroadcastWeeklyLimit ?? "");
      formData.append("enableCarPickupOption", merchantData.carPickupActive);
      formData.append("saveCards", merchantData.saveCards);
      formData.append("moyasarSecretKey", merchantData.moyasarSecretKey);
      formData.append("moyasarWebhookSecret", merchantData.moyasarWebhookSecret);
      formData.append("moyasarPublishableKey", merchantData.moyasarPublishableKey.trim());
      formData.append("paymentGateway", merchantData.paymentGateway);
      formData.append("telrStoreId", merchantData.telrStoreId);
      formData.append("telrApiKey", merchantData.telrApiKey);
      formData.append("telrRemoteAuthKey", merchantData.telrRemoteAuthKey);
      formData.append("telrHostedAuthKey", merchantData.telrHostedAuthKey);
      formData.append("telrWalletAuthKey", merchantData.telrWalletAuthKey);
      formData.append("telrTestMode", merchantData.telrTestMode);
      formData.append("applePayWebEnabled", merchantData.applePayWebEnabled);
      formData.append("applePayMerchantId", merchantData.applePayMerchantId || "");
      formData.append("applePayDisplayName", merchantData.applePayDisplayName || "");
      // Blank = keep the stored pair. The server checks the key belongs to the certificate.
      if (merchantData.applePayIdentityCert?.trim()) {
        formData.append("applePayIdentityCert", merchantData.applePayIdentityCert);
      }
      if (merchantData.applePayIdentityKey?.trim()) {
        formData.append("applePayIdentityKey", merchantData.applePayIdentityKey);
      }
      formData.append("googleMapsApiKey", merchantData.googleMapsApiKey);
      // Only sent when filled in: the server rejects anything that is not a 3-letter
      // code, so posting an empty field would block saving the whole merchant form
      // over a currency nobody was editing.
      if (merchantData.currency) {
        formData.append("currency", merchantData.currency);
      }
      formData.append("forceOnlineOrdersAsDelivery", merchantData.forceOnlineOrdersAsDelivery);
      formData.append("staleTableWarningMinutes", merchantData.staleTableWarningMinutes);
      formData.append("timezone", merchantData.timezone);
      formData.append("enablePosLoyaltyOrders", merchantData.enablePosLoyaltyOrders);
      formData.append("autoCreatePosLoyaltyCustomers", merchantData.autoCreatePosLoyaltyCustomers);
      formData.append("restoreRewardOnPosReturn", merchantData.restoreRewardOnPosReturn);
      formData.append("notificationTarget", merchantData.notificationTarget);
      formData.append("enableCustomerArrival", merchantData.enableCustomerArrival);
      formData.append("waitlistOtpRequired", merchantData.waitlistOtpRequired);
      formData.append("coupons_enabled", merchantData.coupons_enabled);
      formData.append("sendTemplatesToPickupApp", merchantData.sendTemplatesToPickupApp);
      formData.append("virtual_deliverect_sku", merchantData.virtual_deliverect_sku);
      formData.append("hideAppOrdersAnalytics", merchantData.hideAppOrdersAnalytics);
      formData.append("posReceiptTemplatesEnabled", merchantData.posReceiptTemplatesEnabled);
      formData.append("dataType", "merchant");

      const deliveryPartnerPayload = {};
      if (merchantData.clientId) deliveryPartnerPayload.clientId = merchantData.clientId.trim();
      if (merchantData.clientSecret) deliveryPartnerPayload.clientSecret = merchantData.clientSecret.trim();
      if (Object.keys(deliveryPartnerPayload).length) {
        formData.append("deliveryPartner", JSON.stringify(deliveryPartnerPayload));
      }

      const koinzPartnerPayload = {};
      if (merchantData.koinzPosId) koinzPartnerPayload.pos_id = merchantData.koinzPosId.trim();
      if (merchantData.koinzAccessKey) koinzPartnerPayload.access_key = merchantData.koinzAccessKey.trim();
      if (merchantData.koinzMerchantKey) koinzPartnerPayload.merchant_key = merchantData.koinzMerchantKey.trim();
      if (Object.keys(koinzPartnerPayload).length) {
        formData.append("koinzPartner", JSON.stringify(koinzPartnerPayload));
      }

      // Blend. The webhook URL is not here on purpose: Blend issues one per
      // environment, so it is server config (BLEND_WEBHOOK_URL), not merchant data.
      // Brand ID and email are prefilled, so a field that had a value and is now
      // blank is sent as "" = clear. A field that was never set is left out, so
      // merchants without Blend don't get an empty config written.
      const blendPartnerPayload = {};
      for (const [stateKey, jsonKey] of [["blendBrandId", "brandId"], ["blendMerchantEmail", "merchantEmail"]]) {
        const value = (merchantData[stateKey] || "").trim();
        if (value || original?.[stateKey]) blendPartnerPayload[jsonKey] = value;
      }
      if (merchantData.blendMerchantPassword) blendPartnerPayload.merchantPassword = merchantData.blendMerchantPassword.trim();
      // The switch rides along with any Blend change, or on its own when flipped on a merchant
      // that already has Blend. Never on its own for a merchant without Blend: that would write a
      // config for an integration the merchant does not use.
      const blendConfigured = !!(blendStatus.brandId || blendStatus.merchantEmail);
      const blendSyncChanged = merchantData.blendSyncAvailability !== original?.blendSyncAvailability;
      if (Object.keys(blendPartnerPayload).length || (blendSyncChanged && blendConfigured)) {
        blendPartnerPayload.syncAvailability = !!merchantData.blendSyncAvailability;
      }
      if (Object.keys(blendPartnerPayload).length) {
        formData.append("blendPartner", JSON.stringify(blendPartnerPayload));
      }

      if (clearSecrets.size) {
        formData.append("clearSecrets", JSON.stringify([...clearSecrets]));
      }

      formData.append("branchesListColumnsPickupApp", merchantData.branchesListColumnsPickupApp);

      if (imageFile instanceof File) formData.append("image1", imageFile);
      if (branchesPlaceholderFile instanceof File) formData.append("branchesPlaceholderImage", branchesPlaceholderFile);
      if (loyaltyLockedFile instanceof File) formData.append("loyaltyCardLockedImage", loyaltyLockedFile);
      if (loyaltyUnlockedFile instanceof File) formData.append("loyaltyCardUnlockedImage", loyaltyUnlockedFile);
      if (productPlaceholderFile instanceof File) formData.append("productPlaceholderImage", productPlaceholderFile);
      if (kdsDisplayFile instanceof File) {
        formData.append("kdsDisplayImage", kdsDisplayFile);
      } else if (!merchantData.kdsDisplayImage) {
        // Image was removed (and no new one picked) → tell the API to clear the stored path.
        formData.append("kdsDisplayImage", "");
      }
      if (receiptLogoFile instanceof File) {
        formData.append("receiptLogo", receiptLogoFile);
      } else if (!merchantData.receiptLogo) {
        // Cleared (and no new one picked) → tell the API to drop the stored path, so the receipt
        // goes back to printing no mark rather than keeping the old one.
        formData.append("receiptLogo", "");
      }
      if (faviconFile instanceof File) formData.append("favicon", faviconFile);

      const response = await axiosInstance.post(
        `/api/merchant/updateMerchant/${merchantID}`,
        formData,
        { rawRequest: true }
      );

      if (response.data.isError === false) {
        successToast("Merchant updated successfully");
        setReviewOpen(false);
        navigate("/merchant/merchant-list");
      } else {
        errorToast(response.data.message || "Failed to update merchant");
      }
    } catch (error) {
      console.error("Error updating Merchant:", error);
      errorToast(
        error?.response?.data?.message ||
        "An error occurred while updating the merchant."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFetchData = async () => {
    setSyncing((s) => ({ ...s, fetch: true }));
    try {
      if (merchantBranches.length === 0) await getMerchantDetails(merchantID);
      else await refreshMerchantDetails(merchantData.reference);
    } finally {
      setSyncing((s) => ({ ...s, fetch: false }));
    }
  };

  const runSync = async (kind) => {
    if (!merchantData?.reference && kind !== "systemProducts" && kind !== "blendAvailability") return;
    setSyncing((s) => ({ ...s, [kind]: true }));
    try {
      if (kind === "blendAvailability") await resyncAvailability(merchantID, "blend");
      else if (kind === "branches") await syncBranches(merchantData.reference);
      else if (kind === "menu") await syncMenu(merchantData.reference);
      else if (kind === "tables") await syncTables(merchantData.reference);
      else if (kind === "systemProducts") await fetchSystemProducts(merchantID);
    } finally {
      setSyncing((s) => ({ ...s, [kind]: false }));
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Edit Merchant" parent="Merchant" />
      <Container fluid>
        <style>
          {reportingGlobalCSS}
          {`@keyframes rptSpin { to { transform: rotate(360deg); } } .rpt-spin { animation: rptSpin 0.8s linear infinite; }`}
        </style>
        <div className="rpt-wrap">
          {loading ? (
            <RLoader />
          ) : (
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "320px 1fr",
                  gap: 20,
                  alignItems: "start",
                }}
              >
                {/* LEFT: Image + sync actions */}
                <div>
                  <RCard>
                    <SectionTitle>Merchant Image</SectionTitle>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                      <div
                        style={{
                          width: "100%",
                          aspectRatio: "1",
                          borderRadius: 14,
                          border: `2px dashed ${C.border}`,
                          background: C.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={previewImage}
                          alt="merchant"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.target.src = placeholder; }}
                        />
                      </div>
                      <label
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "10px 18px",
                          borderRadius: 10,
                          background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                          color: C.white,
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                        }}
                      >
                        <Upload size={14} />
                        Replace Image
                        <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                      </label>
                    </div>

                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Favicon</div>
                      <div style={{ fontSize: 11, color: C.textMed, marginBottom: 12 }}>
                        Browser tab icon (square PNG / ICO / SVG). Falls back to merchant image if unset.
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 8,
                            border: `2px dashed ${C.border}`,
                            background: C.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={faviconPreview}
                            alt="favicon"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            onError={(e) => { e.target.src = placeholder; }}
                          />
                        </div>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            borderRadius: 10,
                            background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                            color: C.white,
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                          }}
                        >
                          <Upload size={14} />
                          Upload
                          <input
                            type="file"
                            accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/*"
                            onChange={handleFilePreviewChange(setFaviconFile, setFaviconPreview)}
                            style={{ display: "none" }}
                          />
                        </label>
                      </div>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Sync Actions</SectionTitle>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <ActionButton
                        icon={Download}
                        label={syncing.fetch ? "Fetching..." : "Fetch Data"}
                        color={C.green}
                        onClick={handleFetchData}
                        disabled={syncing.fetch}
                        loading={syncing.fetch}
                      />
                      <ActionButton
                        icon={GitBranch}
                        label={syncing.branches ? "Syncing..." : "Sync Branches"}
                        color={C.coral}
                        onClick={() => runSync("branches")}
                        disabled={syncing.branches}
                        loading={syncing.branches}
                      />
                      <ActionButton
                        icon={MenuIcon}
                        label={syncing.menu ? "Syncing..." : "Sync Menu"}
                        color={C.blue}
                        onClick={() => runSync("menu")}
                        disabled={syncing.menu}
                        loading={syncing.menu}
                      />
                      <ActionButton
                        icon={Grid}
                        label={syncing.tables ? "Syncing..." : "Sync Tables"}
                        color={C.purple}
                        onClick={() => runSync("tables")}
                        disabled={syncing.tables}
                        loading={syncing.tables}
                      />
                      <ActionButton
                        icon={RefreshCw}
                        label={syncing.systemProducts ? "Fetching..." : "Fetch System Products"}
                        color={C.orange}
                        onClick={() => runSync("systemProducts")}
                        disabled={syncing.systemProducts}
                        loading={syncing.systemProducts}
                      />
                    </div>
                  </RCard>
                </div>

                {/* RIGHT: Form */}
                <div>
                  <RCard>
                    <SectionTitle>Merchant Details</SectionTitle>

                    {userData?.length > 0 && (
                      <Field label="Customer">
                        <select
                          style={inputStyle}
                          value={merchantData.customer}
                          onChange={(e) => setField("customer", e.target.value)}
                        >
                          <option value="">Select Customer</option>
                          {userData.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.firstName} {c.lastName}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <Field label="Name">
                        <input
                          style={inputDisabledStyle}
                          value={merchantData.name}
                          disabled
                          onChange={(e) => setField("name", e.target.value)}
                        />
                      </Field>
                      <Field label="Reference">
                        <input
                          style={inputDisabledStyle}
                          value={merchantData.reference}
                          disabled
                        />
                      </Field>
                      <Field
                        label="URL Slug"
                        hint={
                          merchantData.slug && !slugError(merchantData.slug, { merchant: true, required: false })
                            ? `Pages: ${PAGES_BASE_URL}/${merchantData.slug}/privacy-policy. Changing it breaks links already shared, such as an App Store listing.`
                            : "Public address for this merchant's pages, e.g. shovel → …/shovel/privacy-policy. Leave empty until pages are needed."
                        }
                      >
                        <input
                          style={{ ...inputStyle, fontFamily: "monospace" }}
                          value={merchantData.slug}
                          placeholder="e.g. shovel"
                          maxLength={60}
                          onChange={(e) => setField("slug", e.target.value.toLowerCase())}
                        />
                        {slugError(merchantData.slug, { merchant: true, required: false }) && (
                          <div style={{ fontSize: 11, color: "#c0392b", marginTop: 4 }}>
                            {slugError(merchantData.slug, { merchant: true, required: false })}
                          </div>
                        )}
                      </Field>
                      <Field label="URL">
                        <input
                          style={inputStyle}
                          value={merchantData.url}
                          onChange={(e) => setField("url", e.target.value)}
                        />
                      </Field>
                      <Field label="Database">
                        <input
                          style={inputStyle}
                          value={merchantData.db}
                          onChange={(e) => setField("db", e.target.value)}
                        />
                      </Field>
                      <Field label="Merchant Email" hint="Login the API uses to connect to this merchant's system.">
                        <input
                          type="text"
                          style={inputStyle}
                          value={merchantData.email}
                          onChange={(e) => setField("email", e.target.value)}
                        />
                      </Field>
                      <Field label="Odoo Password" hint="Leave blank to keep the current password.">
                        <input
                          type="password"
                          autoComplete="new-password"
                          style={inputStyle}
                          placeholder={merchantData.passwordSet ? "•••••••• (set)" : "Not set"}
                          value={merchantData.password}
                          onChange={(e) => setField("password", e.target.value)}
                        />
                      </Field>
                    </div>

                    <Field label="Menu Color">
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          onClick={() => document.getElementById("menuColorPicker").click()}
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            background: merchantData.menuColor || "#ffffff",
                            border: `2px solid ${C.border}`,
                            cursor: "pointer",
                            boxShadow: C.shadow,
                          }}
                        />
                        <Input
                          id="menuColorPicker"
                          type="color"
                          value={merchantData.menuColor || "#ffffff"}
                          onChange={(e) => setField("menuColor", e.target.value)}
                          style={{ display: "none" }}
                        />
                        <div
                          style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            background: C.bg,
                            border: `1px solid ${C.borderLight}`,
                            fontWeight: 600,
                            color: C.textMed,
                            fontSize: 12,
                            letterSpacing: 0.5,
                          }}
                        >
                          {merchantData.menuColor || "#ffffff"}
                        </div>
                      </div>
                    </Field>

                    <Field
                      label="Web KDS — Default Display Background"
                      hint="Panel color for branded Display screens that don't set their own (KDS Devices → Layout)"
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          onClick={() => document.getElementById("kdsDisplayColorPicker").click()}
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            background: merchantData.kdsDisplayColor || "#ffffff",
                            border: `2px solid ${C.border}`,
                            cursor: "pointer",
                            boxShadow: C.shadow,
                          }}
                        />
                        <Input
                          id="kdsDisplayColorPicker"
                          type="color"
                          value={merchantData.kdsDisplayColor || "#ffffff"}
                          onChange={(e) => setField("kdsDisplayColor", e.target.value)}
                          style={{ display: "none" }}
                        />
                        <div
                          style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            background: C.bg,
                            border: `1px solid ${C.borderLight}`,
                            fontWeight: 600,
                            color: C.textMed,
                            fontSize: 12,
                            letterSpacing: 0.5,
                          }}
                        >
                          {merchantData.kdsDisplayColor || "#ffffff"}
                        </div>
                      </div>
                    </Field>

                    {/* Deliberately its own upload rather than reusing the merchant logo above:
                        that one is full colour and sized for a screen, while this is printed with
                        one ink at 180 dots. See the hint. */}
                    <Field
                      label="Receipt Logo (self-order screens)"
                      hint="Printed at the top of the kiosk receipt. Max 180×180px — larger images will not print at all. Use a flat black-on-white or transparent mark; photos and gradients come out blotchy. Leave empty to print no logo."
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div
                          style={{
                            width: 96,
                            height: 96,
                            borderRadius: 10,
                            border: `2px dashed ${C.border}`,
                            background: C.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={receiptLogoPreview}
                            alt="receipt logo"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            onError={(e) => { e.target.src = placeholder; }}
                          />
                        </div>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            borderRadius: 10,
                            background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                            color: C.white,
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                          }}
                        >
                          <Upload size={14} />
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFilePreviewChange(setReceiptLogoFile, setReceiptLogoPreview)}
                            style={{ display: "none" }}
                          />
                        </label>
                        {receiptLogoPreview !== placeholder && (
                          <button
                            type="button"
                            onClick={() => {
                              setReceiptLogoFile(null);
                              setReceiptLogoPreview(placeholder);
                              setField("receiptLogo", "");
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: C.white,
                              border: `1px solid ${C.border}`,
                              color: C.coral,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </Field>

                    <Field
                      label="Web KDS — Default Display Image"
                      hint="Panel image for branded Display screens that don't set their own (KDS Devices → Layout)"
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div
                          style={{
                            width: 140,
                            height: 96,
                            borderRadius: 10,
                            border: `2px dashed ${C.border}`,
                            background: C.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={kdsDisplayPreview}
                            alt="kds display"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => { e.target.src = placeholder; }}
                          />
                        </div>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            borderRadius: 10,
                            background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                            color: C.white,
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                          }}
                        >
                          <Upload size={14} />
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFilePreviewChange(setKdsDisplayFile, setKdsDisplayPreview)}
                            style={{ display: "none" }}
                          />
                        </label>
                        {kdsDisplayPreview !== placeholder && (
                          <button
                            type="button"
                            onClick={() => {
                              setKdsDisplayFile(null);
                              setKdsDisplayPreview(placeholder);
                              setField("kdsDisplayImage", "");
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: C.white,
                              border: `1px solid ${C.border}`,
                              color: C.coral,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </Field>
                  </RCard>

                  <RCard>
                    <SectionTitle>Menu & Loyalty</SectionTitle>
                    <ToggleRow
                      label="Enable Menu Ordering"
                      checked={merchantData.enableMenuOrdering}
                      onChange={(v) => setField("enableMenuOrdering", v)}
                    />
                    <ToggleRow
                      label="Enable Menu Pagination"
                      checked={merchantData.enableMenuPagination}
                      onChange={(v) => setField("enableMenuPagination", v)}
                    />
                    <ToggleRow
                      label="Disable Back to Menus"
                      checked={merchantData.disableBackToMenus}
                      onChange={(v) => setField("disableBackToMenus", v)}
                    />
                    {/* When a product is hidden or snoozed at the branch the customer picked, the
                        app can offer the other branches that still have it. Off by default: turn it
                        on per brand once its menu and branch coverage make the suggestion useful. */}
                    <ToggleRow
                      label="Show Products Available at Other Branches"
                      checked={merchantData.enableBranchProductsAlternatives}
                      onChange={(v) => setField("enableBranchProductsAlternatives", v)}
                    />
                    {/* Master switch for the pickup app's recommendation rails. Pins, rails,
                        pilot branches and the holdout are set on the Recommendations page;
                        this only decides whether customers see anything at all. */}
                    <ToggleRow
                      label="Product Recommendations"
                      checked={merchantData.enableProductRecommendations}
                      onChange={(v) => setField("enableProductRecommendations", v)}
                    />
                    {/* On: after a cart change the app re-applies the customer's coupon and
                        wallet amount instead of silently dropping them. A kill switch only. */}
                    <ToggleRow
                      label="Keep Coupon After Cart Changes"
                      checked={merchantData.enableCartTokenReapply}
                      onChange={(v) => setField("enableCartTokenReapply", v)}
                    />
                    <ToggleRow
                      label="Loyalty Program"
                      checked={merchantData.isLoyaltyActive}
                      onChange={(v) => setField("isLoyaltyActive", v)}
                    />
                    {/* The master switch for membership, beside Loyalty for the same reason
                        Customer Wallet is: the tiers, members and companies live under their
                        own menu, but the feature gate every caller reads is this column on
                        Merchants — createOrder, /membership/verify and /coupon/validate all
                        check it before resolving anything. */}
                    <ToggleRow
                      label="Membership Program"
                      checked={merchantData.isMembershipActive}
                      onChange={(v) => setField("isMembershipActive", v)}
                    />
                    {/* Customer Wallet (stored value) — NOT Apple Wallet passes.
                        The master switch lives here, beside Loyalty, while the limits and top-up
                        packages live under Customer Wallet → Wallet Settings. Same split as
                        enableLoyaltyProgram / MerchantLoyalty: the app's feature gate reads
                        `company.Merchant` from /api/company/details, so it picks this up with no
                        extra plumbing, and a second flag on the config table would only drift. */}
                    <ToggleRow
                      label="Customer Wallet"
                      checked={merchantData.isWalletActive}
                      onChange={(v) => setField("isWalletActive", v)}
                    />
                    <ToggleRow
                      label="Enable POS Loyalty Orders"
                      checked={merchantData.enablePosLoyaltyOrders}
                      onChange={(v) => setField("enablePosLoyaltyOrders", v)}
                    />
                    <ToggleRow
                      label="Auto-create POS Loyalty Customers"
                      checked={merchantData.autoCreatePosLoyaltyCustomers}
                      onChange={(v) => setField("autoCreatePosLoyaltyCustomers", v)}
                    />
                    <ToggleRow
                      label="Restore Reward on POS Return"
                      checked={merchantData.restoreRewardOnPosReturn}
                      onChange={(v) => setField("restoreRewardOnPosReturn", v)}
                    />
                    <ToggleRow
                      label="Enable Coupons Sync (from Merchant POS)"
                      checked={merchantData.coupons_enabled}
                      onChange={(v) => setField("coupons_enabled", v)}
                    />
                  </RCard>

                  <RCard>
                    <SectionTitle>Reporting</SectionTitle>
                    <ToggleRow
                      label="Hide App Orders Analytics report (from this merchant's login)"
                      checked={merchantData.hideAppOrdersAnalytics}
                      onChange={(v) => setField("hideAppOrdersAnalytics", v)}
                    />
                  </RCard>

                  <RCard>
                    <SectionTitle>Printing</SectionTitle>
                    <ToggleRow
                      label="POS receipt templates (Epson printers)"
                      checked={merchantData.posReceiptTemplatesEnabled}
                      onChange={(v) => setField("posReceiptTemplatesEnabled", v)}
                    />
                    <div style={{ fontSize: 12, color: C.textLight, marginTop: -4 }}>
                      Off: the POS prints its built-in receipt. On: it prints the template chosen under Merchants → POS Receipt Templates (Compact until one is set). Sunmi printers keep the built-in receipt for now.
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Pickup App Appearance</SectionTitle>
                    <ToggleRow
                      label="Enable Product Templates"
                      checked={merchantData.sendTemplatesToPickupApp}
                      onChange={(v) => setField("sendTemplatesToPickupApp", v)}
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                      <Field
                        label="Branches Placeholder Image"
                        hint="Shown on the branches list when a branch has no image"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <div
                            style={{
                              width: 96,
                              height: 96,
                              borderRadius: 10,
                              border: `2px dashed ${C.border}`,
                              background: C.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={branchesPlaceholderPreview}
                              alt="branches placeholder"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => { e.target.src = placeholder; }}
                            />
                          </div>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                              color: C.white,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                            }}
                          >
                            <Upload size={14} />
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFilePreviewChange(setBranchesPlaceholderFile, setBranchesPlaceholderPreview)}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </Field>
                      <Field
                        label="Loyalty Card Locked Image"
                        hint="Image shown for unredeemed stamps on the loyalty card"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <div
                            style={{
                              width: 96,
                              height: 96,
                              borderRadius: 10,
                              border: `2px dashed ${C.border}`,
                              background: C.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={loyaltyLockedPreview}
                              alt="loyalty locked"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => { e.target.src = placeholder; }}
                            />
                          </div>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                              color: C.white,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                            }}
                          >
                            <Upload size={14} />
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFilePreviewChange(setLoyaltyLockedFile, setLoyaltyLockedPreview)}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </Field>
                      <Field
                        label="Loyalty Card Unlocked Image"
                        hint="Image shown for redeemed/earned stamps on the loyalty card"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <div
                            style={{
                              width: 96,
                              height: 96,
                              borderRadius: 10,
                              border: `2px dashed ${C.border}`,
                              background: C.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={loyaltyUnlockedPreview}
                              alt="loyalty unlocked"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => { e.target.src = placeholder; }}
                            />
                          </div>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                              color: C.white,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                            }}
                          >
                            <Upload size={14} />
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFilePreviewChange(setLoyaltyUnlockedFile, setLoyaltyUnlockedPreview)}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </Field>
                      <Field
                        label="Product Placeholder Image"
                        hint="Fallback image shown when a product has no image"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <div
                            style={{
                              width: 96,
                              height: 96,
                              borderRadius: 10,
                              border: `2px dashed ${C.border}`,
                              background: C.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={productPlaceholderPreview}
                              alt="product placeholder"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => { e.target.src = placeholder; }}
                            />
                          </div>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 14px",
                              borderRadius: 10,
                              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                              color: C.white,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                            }}
                          >
                            <Upload size={14} />
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFilePreviewChange(setProductPlaceholderFile, setProductPlaceholderPreview)}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </Field>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <Field
                        label="Branches List Columns"
                        hint="Number of branches shown per row in the pickup app (default: 1)"
                      >
                        <input
                          type="number"
                          min="1"
                          max="4"
                          style={inputStyle}
                          value={merchantData.branchesListColumnsPickupApp ?? 1}
                          onChange={(e) => setField("branchesListColumnsPickupApp", e.target.value)}
                        />
                      </Field>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Deliverect Integration</SectionTitle>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <Field label="Client ID">
                        <input
                          style={inputStyle}
                          value={merchantData.clientId}
                          onChange={(e) => setField("clientId", e.target.value)}
                          placeholder="Deliverect Client ID"
                        />
                      </Field>
                      <Field label="Client Secret">
                        <input
                          style={inputStyle}
                          value={merchantData.clientSecret}
                          onChange={(e) => setField("clientSecret", e.target.value)}
                          placeholder="Deliverect Client Secret"
                        />
                      </Field>
                    </div>
                    {merchantData.accessToken && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                        <Field label="Access Token">
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <input
                              style={{ ...inputDisabledStyle, flex: 1 }}
                              value="Access token configured"
                              disabled
                            />
                            <CheckCircle size={20} color={C.green} />
                          </div>
                        </Field>
                        <Field label="Token Expires In">
                          <input
                            style={inputDisabledStyle}
                            value={formatExpiry(merchantData.accessTokenExpiry)}
                            disabled
                          />
                        </Field>
                      </div>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <ToggleRow
                        label="Virtual Deliverect SKU"
                        checked={merchantData.virtual_deliverect_sku}
                        onChange={(v) => setField("virtual_deliverect_sku", v)}
                      />
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Koinz Integration</SectionTitle>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                      Stored credentials are never displayed. Leave a field blank to keep its
                      current value; type in it only to replace that value, or Remove to clear it.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {[
                        { key: "koinzPosId", label: "POS ID", stored: !!koinzStatus.posIdMasked, placeholder: koinzStatus.posIdMasked || "Not set" },
                        { key: "koinzAccessKey", label: "Access Key", stored: koinzStatus.accessKeySet, placeholder: koinzStatus.accessKeySet ? "•••••••• (set)" : "Not set" },
                        { key: "koinzMerchantKey", label: "Merchant Key", stored: koinzStatus.merchantKeySet, placeholder: koinzStatus.merchantKeySet ? "•••••••• (set)" : "Not set" },
                      ].map(({ key, label, stored, placeholder }) => (
                        <Field key={key} label={label}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <input
                              style={clearSecrets.has(key) ? inputDisabledStyle : inputStyle}
                              disabled={clearSecrets.has(key)}
                              value={merchantData[key]}
                              onChange={(e) => setField(key, e.target.value)}
                              placeholder={clearSecrets.has(key) ? "Will be removed on save" : placeholder}
                            />
                            {stored && (
                              <RemoveSecretButton
                                pending={clearSecrets.has(key)}
                                onClick={() => toggleClearSecret(key)}
                              />
                            )}
                          </div>
                        </Field>
                      ))}
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Blend Integration</SectionTitle>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                      Blend reaches this merchant through us only. Give Blend the base
                      {/* REACT_APP_URL carries a trailing slash, so it is stripped
                          rather than assumed either way. */}
                      URL <code>{`${(process.env.REACT_APP_URL || "").replace(/\/+$/, "")}/blend`}</code> and the
                      same merchant user below: we use it to read the menu, and Blend posts it
                      once to our login to collect its access token. Passwords are never
                      displayed — leave it blank to keep the stored one, or Remove to clear it.
                      Delete the Brand ID or email text to clear those.
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <Field
                        label="Brand ID"
                        hint="Optional. Blend sends brand_id on every order and we echo it back; this is only a fallback for an order that carries none. Nothing is filtered by it."
                      >
                        <input
                          style={inputStyle}
                          value={merchantData.blendBrandId}
                          onChange={(e) => setField("blendBrandId", e.target.value)}
                          placeholder={blendStatus.brandId || "Not set"}
                        />
                      </Field>
                      <Field
                        label="Merchant User Email"
                        hint="A dedicated merchant-system user for Blend, so menu reads never disturb the POS sync session. Share this with Blend."
                      >
                        <input
                          style={inputStyle}
                          value={merchantData.blendMerchantEmail}
                          onChange={(e) => setField("blendMerchantEmail", e.target.value)}
                          placeholder={blendStatus.merchantEmail || "Not set"}
                        />
                      </Field>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <Field
                        label="Merchant User Password"
                        hint="Blend receives this too, so keep that user's permissions minimal."
                      >
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            style={clearSecrets.has("blendMerchantPassword") ? inputDisabledStyle : inputStyle}
                            type="password"
                            autoComplete="new-password"
                            disabled={clearSecrets.has("blendMerchantPassword")}
                            value={merchantData.blendMerchantPassword}
                            onChange={(e) => setField("blendMerchantPassword", e.target.value)}
                            placeholder={
                              clearSecrets.has("blendMerchantPassword")
                                ? "Will be removed on save"
                                : blendStatus.merchantPasswordSet ? "•••••••• (set)" : "Not set"
                            }
                          />
                          {blendStatus.merchantPasswordSet && (
                            <RemoveSecretButton
                              pending={clearSecrets.has("blendMerchantPassword")}
                              onClick={() => toggleClearSecret("blendMerchantPassword")}
                            />
                          )}
                        </div>
                      </Field>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <ToggleRow
                        label="Push availability to Blend (snoozed products, paused or inactive branches)"
                        checked={merchantData.blendSyncAvailability}
                        onChange={(v) => setField("blendSyncAvailability", v)}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <ActionButton
                          icon={RefreshCw}
                          label={syncing.blendAvailability ? "Queuing..." : "Resend Availability to Blend"}
                          color={C.blue}
                          onClick={() => runSync("blendAvailability")}
                          disabled={syncing.blendAvailability || !blendStatus.brandId || !original?.blendSyncAvailability}
                          loading={syncing.blendAvailability}
                        />
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Sends every branch status and current snooze again. Use after Blend's webhook
                          was down or its menu looks out of sync. Needs a saved Brand ID with the switch on.
                        </span>
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>
                      Merchant session:{" "}
                      {blendStatus.sessionSet
                        ? `active${blendStatus.sessionFetchedAt
                          ? ` (refreshed ${new Date(blendStatus.sessionFetchedAt).toLocaleString()})`
                          : ""}`
                        : "not established yet — it is created on the first menu request"}
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Google Maps</SectionTitle>

                    <Field
                      label="Distance Matrix API Key"
                      hint="Used server-side to show customers the driving distance to each branch in the Pickup App. Billed to this merchant's own Google account. Leave blank to keep the current key."
                    >
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          style={clearSecrets.has("googleMapsApiKey") ? inputDisabledStyle : inputStyle}
                          type="password"
                          autoComplete="new-password"
                          disabled={clearSecrets.has("googleMapsApiKey")}
                          placeholder={
                            clearSecrets.has("googleMapsApiKey")
                              ? "Will be removed on save"
                              : merchantData.googleMapsApiKeySet
                                ? "•••••••••• (configured)"
                                : "AIza..."
                          }
                          value={merchantData.googleMapsApiKey}
                          onChange={(e) => setField("googleMapsApiKey", e.target.value)}
                        />
                        {merchantData.googleMapsApiKeySet && (
                          <RemoveSecretButton
                            pending={clearSecrets.has("googleMapsApiKey")}
                            onClick={() => toggleClearSecret("googleMapsApiKey")}
                          />
                        )}
                      </div>
                    </Field>
                    <div style={{ fontSize: 12, color: clearSecrets.has("googleMapsApiKey") ? C.coral : C.textMed }}>
                      {clearSecrets.has("googleMapsApiKey")
                        ? "Will be removed on save."
                        : merchantData.googleMapsApiKeySet
                          ? "A key is configured. Enter a new value to replace it, or Remove to clear it."
                          : "No key set — branches are ordered by straight-line distance instead, with no distance label."}
                    </div>
                  </RCard>

                  {/* Gateway selection governs BOTH blocks below, so it lives on its
                      own rather than inside one of them. */}
                  <RCard>
                    <SectionTitle>Payment Gateway</SectionTitle>

                    <Field
                      label="Active Payment Gateway"
                      hint="Which provider this merchant charges cards through. Only the selected gateway's settings are shown below."
                    >
                      <select
                        style={inputStyle}
                        value={merchantData.paymentGateway}
                        onChange={(e) => setField("paymentGateway", e.target.value)}
                      >
                        <option value="moyasar">Moyasar</option>
                        <option value="telr">Telr</option>
                      </select>
                    </Field>

                    {merchantData.paymentGateway === "telr" && !merchantData.telrApiKeySet && !merchantData.telrApiKey && (
                      <div style={{ fontSize: 12, color: C.danger || "#b42318", marginBottom: 10 }}>
                        Telr is selected but no API Key is set — save will be rejected until one is entered.
                      </div>
                    )}

                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.textMed, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={showAllGateways}
                        onChange={(e) => setShowAllGateways(e.target.checked)}
                      />
                      Show both gateways' settings (to configure one before switching to it)
                    </label>
                  </RCard>

                  {(showAllGateways || merchantData.paymentGateway !== "telr") && (
                  <RCard>
                    <SectionTitle>
                      Moyasar Payment{merchantData.paymentGateway !== "moyasar" ? " (not active)" : ""}
                    </SectionTitle>
                    <Field
                      label="Publishable API Key"
                      hint="Used by the Pickup App to take payments for this merchant. Must be from the same Moyasar account as the Secret API Key below. Leave blank to use the app's built-in key."
                    >
                      <input
                        style={inputStyle}
                        autoComplete="off"
                        spellCheck={false}
                        value={merchantData.moyasarPublishableKey}
                        onChange={(e) => setField("moyasarPublishableKey", e.target.value)}
                        placeholder="pk_live_..."
                      />
                    </Field>
                    <Field
                      label="Secret API Key"
                      hint="Used server-side to issue refunds for this merchant. Leave blank to keep the current key."
                    >
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          style={clearSecrets.has("moyasarSecretKey") ? inputDisabledStyle : inputStyle}
                          type="password"
                          autoComplete="new-password"
                          disabled={clearSecrets.has("moyasarSecretKey")}
                          placeholder={
                            clearSecrets.has("moyasarSecretKey")
                              ? "Will be removed on save"
                              : merchantData.moyasarSecretKeySet
                                ? "•••••••••• (configured)"
                                : "sk_live_..."
                          }
                          value={merchantData.moyasarSecretKey}
                          onChange={(e) => setField("moyasarSecretKey", e.target.value)}
                        />
                        {merchantData.moyasarSecretKeySet && (
                          <RemoveSecretButton
                            pending={clearSecrets.has("moyasarSecretKey")}
                            onClick={() => toggleClearSecret("moyasarSecretKey")}
                          />
                        )}
                      </div>
                    </Field>
                    <div style={{ fontSize: 12, color: clearSecrets.has("moyasarSecretKey") ? C.coral : C.textMed }}>
                      {clearSecrets.has("moyasarSecretKey")
                        ? "Will be removed on save."
                        : merchantData.moyasarSecretKeySet
                          ? "A secret key is configured. Enter a new value to replace it, or Remove to clear it."
                          : "No secret key set — payments, refunds and top-ups for this merchant cannot be verified with Moyasar."}
                    </div>

                    <Field
                      label="Webhook Secret Token"
                      hint="The Secret Token set in this merchant's Moyasar dashboard webhook. Identifies + verifies their webhooks. Leave blank to keep the current one."
                    >
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          style={clearSecrets.has("moyasarWebhookSecret") ? inputDisabledStyle : inputStyle}
                          type="password"
                          autoComplete="new-password"
                          disabled={clearSecrets.has("moyasarWebhookSecret")}
                          placeholder={
                            clearSecrets.has("moyasarWebhookSecret")
                              ? "Will be removed on save"
                              : merchantData.moyasarWebhookSecretSet
                                ? "•••••••••• (configured)"
                                : "webhook secret token"
                          }
                          value={merchantData.moyasarWebhookSecret}
                          onChange={(e) => setField("moyasarWebhookSecret", e.target.value)}
                        />
                        {merchantData.moyasarWebhookSecretSet && (
                          <RemoveSecretButton
                            pending={clearSecrets.has("moyasarWebhookSecret")}
                            onClick={() => toggleClearSecret("moyasarWebhookSecret")}
                          />
                        )}
                      </div>
                    </Field>
                    <div style={{ fontSize: 12, color: clearSecrets.has("moyasarWebhookSecret") ? C.coral : C.textMed }}>
                      {clearSecrets.has("moyasarWebhookSecret")
                        ? "Will be removed on save."
                        : merchantData.moyasarWebhookSecretSet
                          ? "A webhook secret is configured. Enter a new value to replace it, or Remove to clear it."
                          : "No webhook secret set — this merchant's webhooks are matched via the global secret + payment metadata."}
                    </div>
                  </RCard>
                  )}

                  {(showAllGateways || merchantData.paymentGateway === "telr") && (
                  <RCard>
                    <SectionTitle>
                      Telr Payment{merchantData.paymentGateway !== "telr" ? " (not active)" : ""}
                    </SectionTitle>

                    <Field
                      label="Store ID"
                      hint="Telr Store ID, e.g. 35397. Not a secret — used as the API username."
                    >
                      <input
                        style={inputStyle}
                        value={merchantData.telrStoreId}
                        onChange={(e) => setField("telrStoreId", e.target.value)}
                        placeholder="35397"
                      />
                    </Field>

                    <Field
                      label="Mobile SDK API Key"
                      hint="From Telr: Integrations > Mobile SDK > Authentication key. This takes payments. Leave blank to keep the current key."
                    >
                      <input
                        style={inputStyle}
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          merchantData.telrApiKeySet
                            ? "•••••••••• (configured)"
                            : "Mobile SDK authentication key"
                        }
                        value={merchantData.telrApiKey}
                        onChange={(e) => setField("telrApiKey", e.target.value)}
                      />
                    </Field>
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      {merchantData.telrApiKeySet
                        ? "An API key is configured. Enter a new value to replace it."
                        : "No API key set — this merchant cannot take Telr payments."}
                    </div>

                    <Field
                      label="Remote API Key"
                      hint="A SEPARATE key, only for refunds. Telr support must enable Remote API on the store before it exists. Leave blank to keep the current one."
                    >
                      <input
                        style={inputStyle}
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          merchantData.telrRemoteAuthKeySet
                            ? "•••••••••• (configured)"
                            : "Remote API authentication key"
                        }
                        value={merchantData.telrRemoteAuthKey}
                        onChange={(e) => setField("telrRemoteAuthKey", e.target.value)}
                      />
                    </Field>
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      {merchantData.telrRemoteAuthKeySet
                        ? "A Remote API key is configured — refunds can be issued from here."
                        : "No Remote API key — Telr refunds will be refused and must be done in the Telr dashboard."}
                    </div>

                    <Field
                      label="Hosted Payment Page Key"
                      hint="A SEPARATE key for web checkout (QR menu). From Telr: Integrations > Hosted Payment Page > Authentication key. Leave blank to keep the current one."
                    >
                      <input
                        style={inputStyle}
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          merchantData.telrHostedAuthKeySet
                            ? "•••••••••• (configured)"
                            : "Hosted Payment Page authentication key"
                        }
                        value={merchantData.telrHostedAuthKey}
                        onChange={(e) => setField("telrHostedAuthKey", e.target.value)}
                      />
                    </Field>
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      {merchantData.telrHostedAuthKeySet
                        ? "A Hosted Payment Page key is configured — branches can take online payment in the QR menu."
                        : "No Hosted Payment Page key — branches of this merchant cannot be set to online menu payment."}
                    </div>

                    <Field
                      label="Wallets Key"
                      hint="A SEPARATE key for Apple Pay on the web menu. From Telr: Integrations > Wallets > Authentication key. (The Remote API key above only covers refunds.) Leave blank to keep the current one."
                    >
                      <input
                        style={inputStyle}
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          merchantData.telrWalletAuthKeySet
                            ? "•••••••••• (configured)"
                            : "Wallets authentication key"
                        }
                        value={merchantData.telrWalletAuthKey}
                        onChange={(e) => setField("telrWalletAuthKey", e.target.value)}
                      />
                    </Field>
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      {merchantData.telrWalletAuthKeySet
                        ? "A Wallets key is configured — Apple Pay payments on the web menu can be charged."
                        : "No Wallets key — Apple Pay on the web menu cannot be switched on."}
                    </div>

                    <Field
                      label="Currency"
                      hint="Charge currency, 3-letter code. The customer app is SAR-only for now."
                    >
                      <input
                        style={inputStyle}
                        value={merchantData.currency}
                        onChange={(e) => setField("currency", e.target.value.toUpperCase().slice(0, 3))}
                        placeholder="SAR"
                      />
                    </Field>

                    <ToggleRow
                      label="Telr Test Mode"
                      checked={merchantData.telrTestMode}
                      onChange={(v) => setField("telrTestMode", v)}
                    />
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      {merchantData.telrTestMode
                        ? "Test mode — transactions are not real. Turn this OFF to take live payments."
                        : "LIVE mode — real cards will be charged."}
                    </div>

                    <SectionTitle>Apple Pay on web menu</SectionTitle>
                    <div style={{ fontSize: 12, color: C.textMed, marginBottom: 10 }}>
                      Shows an Apple Pay button on the QR menu (Safari). Uses this merchant's own Apple
                      merchant ID — the one whose Payment Processing certificate is already at Telr — and
                      needs the Telr Wallets key above. The menu domain must be verified under that
                      merchant ID in Apple Developer.
                    </div>

                    <ToggleRow
                      label="Apple Pay on web menu"
                      checked={merchantData.applePayWebEnabled}
                      onChange={(v) => setField("applePayWebEnabled", v)}
                    />

                    <Field
                      label="Apple Merchant ID"
                      hint="From Apple Developer → Identifiers → Merchant IDs, e.g. merchant.com.telr.brgr."
                    >
                      <input
                        style={inputStyle}
                        value={merchantData.applePayMerchantId}
                        onChange={(e) => setField("applePayMerchantId", e.target.value.trim())}
                        placeholder="merchant.com.example"
                      />
                    </Field>

                    <Field label="Name on the Apple Pay sheet" hint="Shown to the customer. Defaults to the merchant name.">
                      <input
                        style={inputStyle}
                        value={merchantData.applePayDisplayName}
                        maxLength={64}
                        onChange={(e) => setField("applePayDisplayName", e.target.value)}
                        placeholder={merchantData.name || "Merchant name"}
                      />
                    </Field>

                    <Field
                      label="Merchant Identity Certificate (PEM)"
                      hint="Apple Developer → the merchant ID → Apple Pay Merchant Identity Certificate, converted to PEM. Starts with BEGIN CERTIFICATE. Leave blank to keep the current one."
                    >
                      <textarea
                        style={{ ...inputStyle, minHeight: 90, fontFamily: "monospace", fontSize: 11 }}
                        value={merchantData.applePayIdentityCert}
                        onChange={(e) => setField("applePayIdentityCert", e.target.value)}
                        placeholder={
                          merchantData.applePayIdentitySet
                            ? "•••••••••• (configured)"
                            : "-----BEGIN CERTIFICATE-----"
                        }
                      />
                    </Field>

                    <Field
                      label="Merchant Identity Private Key (PEM)"
                      hint="The private key the certificate request was made with. Starts with BEGIN … PRIVATE KEY. Never share it in chat or email."
                    >
                      <textarea
                        style={{ ...inputStyle, minHeight: 90, fontFamily: "monospace", fontSize: 11 }}
                        value={merchantData.applePayIdentityKey}
                        onChange={(e) => setField("applePayIdentityKey", e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={
                          merchantData.applePayIdentitySet
                            ? "•••••••••• (configured)"
                            : "-----BEGIN PRIVATE KEY-----"
                        }
                      />
                    </Field>
                    <div
                      style={{
                        fontSize: 12,
                        color:
                          merchantData.applePayIdentityDaysLeft !== null && merchantData.applePayIdentityDaysLeft < 30
                            ? C.danger || "#b42318"
                            : C.textMed,
                      }}
                    >
                      {merchantData.applePayIdentitySet
                        ? merchantData.applePayIdentityDaysLeft !== null
                          ? `Identity certificate configured — expires in ${merchantData.applePayIdentityDaysLeft} days.`
                          : "Identity certificate configured."
                        : "No identity certificate — Apple Pay cannot be switched on."}
                    </div>
                  </RCard>
                  )}

                  <RCard>
                    <SectionTitle>Online Orders</SectionTitle>
                    <ToggleRow
                      label="Car Pickup"
                      checked={merchantData.carPickupActive}
                      onChange={(v) => setField("carPickupActive", v)}
                    />
                    <ToggleRow
                      label="Save Cards"
                      checked={merchantData.saveCards}
                      onChange={(v) => setField("saveCards", v)}
                    />
                    <ToggleRow
                      label="Force Online Orders as Delivery"
                      checked={merchantData.forceOnlineOrdersAsDelivery}
                      onChange={(v) => setField("forceOnlineOrdersAsDelivery", v)}
                    />
                    <ToggleRow
                      label="Block Checkout on Unavailable Items"
                      checked={merchantData.blockOrderOnUnavailableItems}
                      onChange={(v) => setField("blockOrderOnUnavailableItems", v)}
                    />
                    <ToggleRow
                      label="Enable Customer Arrival"
                      checked={merchantData.enableCustomerArrival}
                      onChange={(v) => setField("enableCustomerArrival", v)}
                    />
                    <ToggleRow
                      label="Require OTP for Waitlist (QR self-join)"
                      checked={merchantData.waitlistOtpRequired}
                      onChange={(v) => setField("waitlistOtpRequired", v)}
                    />
                    <Field label="Notification Destination" hint="Where customer arrival notifications are shown">
                      <select
                        style={inputStyle}
                        value={merchantData.notificationTarget || "pos"}
                        onChange={(e) => setField("notificationTarget", e.target.value)}
                      >
                        <option value="pos">POS</option>
                        <option value="kds">KDS</option>
                      </select>
                    </Field>
                    <ToggleRow
                      label="Accept Normal Orders on Creation"
                      checked={merchantData.acceptNormalOrdersOnCreation}
                      onChange={(v) => setField("acceptNormalOrdersOnCreation", v)}
                    />
                    <div style={{ fontSize: 12, color: C.textMed, marginTop: -4, marginBottom: 10 }}>
                      {merchantData.acceptNormalOrdersOnCreation
                        ? "ON — Pickup App orders are marked accepted by the server the moment they are placed: loyalty and the \"Order Accepted\" notification fire immediately, no POS tap needed. For merchants without a POS (testing, demo, KDS-only). This is not the POS auto-accept setting."
                        : "OFF — orders wait in \"pending\" until the POS, KDS or an admin accepts them (normal flow)."}
                    </div>
                    <ToggleRow
                      label="Customer Feedback QR on Receipts"
                      checked={merchantData.customerFeedbackEnabled}
                      onChange={(v) => setField("customerFeedbackEnabled", v)}
                    />
                    <div style={{ fontSize: 12, color: C.textMed, marginTop: -4, marginBottom: 10 }}>
                      {merchantData.customerFeedbackEnabled
                        ? "ON — the server accepts feedback from receipt QR codes and syncs it to Odoo. The QR is only PRINTED when \"customer feedback\" is also enabled on the Odoo company record; both switches must be on for the flow to work end to end."
                        : "OFF — feedback links are rejected. Turn this off to retire the feature without waiting for already-printed receipts to disappear."}
                    </div>
                    {/* Nested: it only has meaning while the feature above is on. */}
                    {merchantData.customerFeedbackEnabled && (
                      <div style={{ paddingLeft: 14, borderLeft: `2px solid ${C.border}`, marginBottom: 10 }}>
                        <ToggleRow
                          label="Require Customer Details on the Feedback Form"
                          checked={merchantData.customerFeedbackContactRequired}
                          onChange={(v) => setField("customerFeedbackContactRequired", v)}
                        />
                        <div style={{ fontSize: 12, color: C.textMed, marginTop: -4 }}>
                          {merchantData.customerFeedbackContactRequired
                            ? "ON — name and mobile number must both be filled in before feedback can be sent. Only applies to walk-ins: when the receipt already carried the customer, the form never asks. The trade-off is real — a customer who will not leave a number leaves no rating either."
                            : "OFF — name and mobile are optional. Anonymous ratings still come through; you just cannot follow up on them."}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
                      <Field label="Email Pending Orders Count">
                        <input
                          type="number"
                          style={inputStyle}
                          value={merchantData.email_pending_orders_count ?? 0}
                          onChange={(e) => setField("email_pending_orders_count", e.target.value)}
                        />
                      </Field>
                      <Field
                        label="Stale Device Orders Alert (days)"
                        hint="Email + notify every 12 h while a tablet still holds unsynced or open orders older than this many days. Cancelled orders that were never cleared off the till are reported but never trigger it. 0 = off."
                      >
                        <input
                          type="number"
                          min="0"
                          max="365"
                          style={inputStyle}
                          value={merchantData.staleDeviceOrdersAlertDays ?? 0}
                          onChange={(e) => setField("staleDeviceOrdersAlertDays", e.target.value)}
                        />
                      </Field>
                      <Field label="Pending Orders Cancellation Minutes">
                        <input
                          type="number"
                          min="0"
                          style={inputStyle}
                          value={merchantData.pendingOrdersCancellationMinutes ?? 0}
                          onChange={(e) => setField("pendingOrdersCancellationMinutes", e.target.value)}
                        />
                      </Field>
                      <Field label="Auto Ready Minutes">
                        <input
                          type="number"
                          style={inputStyle}
                          value={merchantData.autoReadyMinutes ?? 0}
                          onChange={(e) => setField("autoReadyMinutes", e.target.value)}
                        />
                      </Field>
                      <Field label="Auto Delivered Minutes">
                        <input
                          type="number"
                          style={inputStyle}
                          value={merchantData.autoDeliveredMinutes ?? 0}
                          onChange={(e) => setField("autoDeliveredMinutes", e.target.value)}
                        />
                      </Field>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Marketing Limits</SectionTitle>
                    <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12 }}>
                      How many marketing messages this merchant may send in any rolling 7 days.
                      Leave empty for the platform default (3). Set 0 to switch a channel off.
                      Test sends and silent wallet refreshes are never counted.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <Field
                        label="Push campaign sends / week"
                        hint="Marketing push notifications to the pickup app"
                      >
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          placeholder="Default (3)"
                          style={inputStyle}
                          value={merchantData.pushCampaignWeeklyLimit ?? ""}
                          onChange={(e) => setField("pushCampaignWeeklyLimit", e.target.value)}
                        />
                      </Field>
                      <Field
                        label="Wallet offer announcements / week"
                        hint="Apple Wallet pushes that change the offer text and notify card holders"
                      >
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          placeholder="Default (3)"
                          style={inputStyle}
                          value={merchantData.walletBroadcastWeeklyLimit ?? ""}
                          onChange={(e) => setField("walletBroadcastWeeklyLimit", e.target.value)}
                        />
                      </Field>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Table Management</SectionTitle>
                    <Field
                      label="Timezone"
                      hint="Used for reservation notifications and dashboard timing"
                    >
                      <select
                        style={inputStyle}
                        value={merchantData.timezone || "Asia/Riyadh"}
                        onChange={(e) => setField("timezone", e.target.value)}
                      >
                        <option value="Asia/Riyadh">Asia/Riyadh (UTC+3)</option>
                        <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
                        <option value="Asia/Qatar">Asia/Qatar (UTC+3)</option>
                        <option value="Asia/Bahrain">Asia/Bahrain (UTC+3)</option>
                        <option value="Asia/Kuwait">Asia/Kuwait (UTC+3)</option>
                        <option value="Asia/Muscat">Asia/Muscat (UTC+4)</option>
                        <option value="Africa/Cairo">Africa/Cairo (UTC+2)</option>
                        <option value="Europe/London">Europe/London (UTC+0/+1)</option>
                        <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                        <option value="Asia/Karachi">Asia/Karachi (UTC+5)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (UTC+5:30)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </Field>
                    <Field
                      label="Stale Table Warning (minutes)"
                      hint="Tables occupied longer than this will show a stale warning on POS (default: 180 min / 3 hours)"
                    >
                      <input
                        type="number"
                        min="0"
                        style={inputStyle}
                        value={merchantData.staleTableWarningMinutes ?? 180}
                        onChange={(e) => setField("staleTableWarningMinutes", e.target.value)}
                      />
                    </Field>
                  </RCard>

                  <RCard>
                    <SectionTitle>Wallet Pass (Loyalty Card)</SectionTitle>
                    <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.5 }}>
                      Apple Wallet loyalty-card branding (colors &amp; icons) is now managed on the dedicated{" "}
                      <b>Loyalty → Card Design</b> page, which shows a live preview as you edit.
                    </div>
                  </RCard>

                  <RCard>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <Button
                        type="button"
                        onClick={() => navigate("/merchant/merchant-list")}
                        style={{
                          padding: "10px 22px",
                          borderRadius: 10,
                          background: C.white,
                          color: C.text,
                          border: `1.5px solid ${C.border}`,
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        Discard
                      </Button>
                      <Button
                        type="submit"
                        disabled={saving}
                        style={{
                          padding: "10px 24px",
                          borderRadius: 10,
                          background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                          border: "none",
                          color: C.white,
                          fontWeight: 700,
                          fontSize: 13,
                          boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </RCard>
                </div>
              </div>
            </form>
          )}
        </div>
        <MerchantChangesReviewModal
          isOpen={reviewOpen}
          toggle={() => setReviewOpen(false)}
          rows={reviewRows}
          saving={saving}
          onConfirm={doSave}
        />
      </Container>
    </Fragment>
  );
};

export default EditMerchant;
